import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoMedia from "@/components/PhotoMedia";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Heart, MessageCircle, Share2, Home, Send, Play, Hash, X, CornerDownRight, Bookmark, Music } from "lucide-react";
import { initials, timeAgo } from "@/lib/format";
import { toast } from "sonner";

const LIMIT = 6;
const randSeed = () => Math.floor(Math.random() * 1e9);

const Feed = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTag = searchParams.get("tag") || "";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seed, setSeed] = useState(randSeed());
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tag, setTag] = useState(initialTag);
  const [soundFilter, setSoundFilter] = useState("");
  const [trending, setTrending] = useState([]);
  const [commentsFor, setCommentsFor] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef();
  const audioRef = useRef(null);
  const itemRefs = useRef({});
  const stateRef = useRef({});
  stateRef.current = { skip, seed, hasMore, loadingMore, tag, sound: soundFilter, count: posts.length };

  const fetchPage = useCallback(async ({ reset, useSeed, useSkip, useTag, useSound }) => {
    const { data } = await api.get(`/feed`, { params: { skip: useSkip, limit: LIMIT, seed: useSeed, tag: useTag, sound: useSound } });
    const withKeys = data.posts.map((p, i) => ({ ...p, _key: `${useSeed}-${useSkip}-${p.id}-${i}` }));
    setPosts((prev) => (reset ? withKeys : [...prev, ...withKeys]));
    setSkip(data.next_skip);
    setHasMore(data.has_more);
    return data;
  }, []);

  const reload = useCallback(async (opts = {}) => {
    const nextTag = opts.tag !== undefined ? opts.tag : tag;
    const nextSound = opts.sound !== undefined ? opts.sound : soundFilter;
    setLoading(true);
    const s = randSeed();
    setSeed(s);
    try {
      await fetchPage({ reset: true, useSeed: s, useSkip: 0, useTag: nextTag, useSound: nextSound });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [fetchPage, tag, soundFilter]);

  useEffect(() => { reload({ tag: initialTag, sound: "" }); api.get("/feed/trending").then(({ data }) => setTrending(data.trending)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  // Track which video is centered in the viewport (active) via IntersectionObserver.
  useEffect(() => {
    if (!posts.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.6) {
          setActiveId(e.target.getAttribute("data-postid"));
        }
      });
    }, { threshold: [0.6], root: containerRef.current });
    Object.values(itemRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [posts]);

  // Play the active post's music track (30s preview), looped, at its chosen volume.
  useEffect(() => {
    const post = posts.find((p) => p.id === activeId);
    const url = post?.sound_preview_url;
    if (!audioRef.current) { audioRef.current = new Audio(); audioRef.current.loop = true; }
    const audio = audioRef.current;
    if (!url) { audio.pause(); return; }
    if (audio.src !== url) { audio.src = url; }
    audio.volume = typeof post.sound_volume === "number" ? post.sound_volume : 0.8;
    const tryPlay = () => audio.play().catch(() => {});
    tryPlay();
    // Resume on first user gesture if autoplay was blocked.
    const resume = () => { tryPlay(); };
    document.addEventListener("pointerdown", resume, { once: true });
    return () => { document.removeEventListener("pointerdown", resume); };
  }, [activeId, posts]);

  // Stop audio when leaving the feed.
  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  const loadMore = useCallback(async () => {
    const st = stateRef.current;
    if (st.loadingMore) return;
    setLoadingMore(true);
    try {
      if (st.hasMore) {
        await fetchPage({ reset: false, useSeed: st.seed, useSkip: st.skip, useTag: st.tag, useSound: st.sound });
      } else if (st.count > 0) {
        const s = randSeed();
        setSeed(s);
        await fetchPage({ reset: false, useSeed: s, useSkip: 0, useTag: st.tag, useSound: st.sound });
      }
    } catch (e) {}
    finally { setLoadingMore(false); }
  }, [fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight * 1.5) loadMore();
  };

  const pickTag = (t) => {
    const nt = tag === t ? "" : t;
    setTag(nt); setSoundFilter("");
    if (containerRef.current) containerRef.current.scrollTop = 0;
    reload({ tag: nt, sound: "" });
  };

  const pickSound = (s) => {
    const ns = soundFilter === s ? "" : s;
    setSoundFilter(ns); setTag("");
    if (containerRef.current) containerRef.current.scrollTop = 0;
    reload({ tag: "", sound: ns });
  };

  const toggleSave = async (post) => {
    try {
      const { data } = await api.post(`/feed/${post.id}/save`);
      setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, saved_by_me: data.saved } : p));
      toast.success(data.saved ? "Ajouté à vos favoris" : "Retiré des favoris");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleLike = async (post) => {
    try {
      const { data } = await api.post(`/feed/${post.id}/like`);
      setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, liked_by_me: data.liked, like_count: data.like_count } : p));
    } catch (e) { toast.error(apiError(e)); }
  };

  const openComments = async (post) => {
    setCommentsFor(post);
    setComments([]);
    setReplyTo(null);
    try {
      const { data } = await api.get(`/feed/${post.id}/comments`);
      setComments(data.comments);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    const text = newComment.trim();
    setNewComment("");
    const parent_id = replyTo?.id || null;
    try {
      const { data } = await api.post(`/feed/${commentsFor.id}/comments`, { text, parent_id });
      if (parent_id) {
        setComments((cs) => cs.map((c) => c.id === parent_id ? { ...c, replies: [...(c.replies || []), data.comment] } : c));
      } else {
        setComments((c) => [data.comment, ...c]);
      }
      setPosts((ps) => ps.map((p) => p.id === commentsFor.id ? { ...p, comment_count: data.comment_count } : p));
      setReplyTo(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const likeComment = async (comment, parentId = null) => {
    try {
      const { data } = await api.post(`/feed/comments/${comment.id}/like`);
      setComments((cs) => cs.map((c) => {
        if (parentId && c.id === parentId) {
          return { ...c, replies: c.replies.map((r) => r.id === comment.id ? { ...r, liked_by_me: data.liked, like_count: data.like_count } : r) };
        }
        if (!parentId && c.id === comment.id) return { ...c, liked_by_me: data.liked, like_count: data.like_count };
        return c;
      }));
    } catch (e) {}
  };

  const share = async (post) => {
    const url = `${window.location.origin}/c/${post.sender?.username}`;
    try {
      if (navigator.share) await navigator.share({ title: `MIHOD PAY — @${post.sender?.username}`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
    } catch (e) {}
  };

  const openUnlock = (post) => { api.post(`/photos/${post.id}/view`).catch(() => {}); };

  const renderRich = (text) =>
    (text || "").split(/(\s+)/).map((part, i) => {
      if (/^[@#]\w+/.test(part)) return <span key={i} className="text-primary font-medium">{part}</span>;
      return <span key={i}>{part}</span>;
    });

  const CommentRow = ({ c, parentId = null, isReply = false }) => (
    <div className={`flex items-start gap-2 ${isReply ? "ml-9" : ""}`}>
      <Avatar className="h-8 w-8">
        {c.user?.avatar && <AvatarImage src={c.user.avatar} alt={c.user.username} />}
        <AvatarFallback className="bg-accent text-accent-foreground text-[10px]">{initials(c.user?.username)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm"><span className="font-medium">@{c.user?.username}</span> <span className="text-[11px] text-muted-foreground">{timeAgo(c.created_at)}</span></p>
        <p className="text-sm text-muted-foreground break-words">{renderRich(c.text)}</p>
        {!isReply && (
          <button onClick={() => setReplyTo({ id: c.id, username: c.user?.username })} className="text-[11px] text-muted-foreground hover:text-primary mt-0.5 flex items-center gap-1" data-testid={`comment-reply-${c.id}`}>
            <CornerDownRight className="h-3 w-3" /> Répondre
          </button>
        )}
      </div>
      <button onClick={() => likeComment(c, parentId)} className="flex flex-col items-center text-muted-foreground" data-testid={`comment-like-${c.id}`}>
        <Heart className={`h-4 w-4 ${c.liked_by_me ? "fill-[hsl(0_92%_62%)] text-[hsl(0_92%_62%)]" : ""}`} />
        {c.like_count > 0 && <span className="text-[10px]">{c.like_count}</span>}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black z-40">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-20 bg-gradient-to-b from-black/70 to-transparent pb-4">
        <div className="h-14 flex items-center justify-between px-4">
          <span className="text-white font-display text-lg flex items-center gap-2"><Play className="h-5 w-5 text-[hsl(214_100%_62%)] fill-[hsl(214_100%_62%)]" /> Découvrir</span>
          <button onClick={() => navigate("/")} className="h-9 w-9 grid place-items-center rounded-xl bg-white/10 text-white" data-testid="feed-home-button" aria-label="Accueil">
            <Home className="h-5 w-5" />
          </button>
        </div>
        {/* Trending challenges */}
        {(trending.length > 0 || soundFilter) && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar px-4" data-testid="feed-trending">
            {soundFilter && (
              <button onClick={() => pickSound(soundFilter)} className="shrink-0 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-primary text-primary-foreground" data-testid="feed-active-sound">
                <Music className="h-3 w-3" />{soundFilter}<X className="h-3 w-3" />
              </button>
            )}
            {trending.map((tr) => (
              <button key={tr.tag} onClick={() => pickTag(tr.tag)} data-testid={`feed-tag-${tr.tag}`}
                className={`shrink-0 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${tag === tr.tag ? "bg-primary text-primary-foreground" : "bg-white/12 text-white"}`}>
                <Hash className="h-3 w-3" />{tr.tag}<span className="opacity-60">{tr.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-full grid place-items-center text-white/70">Chargement…</div>
      ) : posts.length === 0 ? (
        <div className="h-full grid place-items-center text-center text-white/70 px-8">
          <div>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-white/10 grid place-items-center mb-3"><Play className="h-6 w-6" /></div>
            <p className="font-medium text-white">{tag ? `Aucune vidéo #${tag}` : "Aucune vidéo pour l'instant"}</p>
            <p className="text-sm mt-1">Publiez une vidéo sur votre mur pour lancer le feed.</p>
            <Button className="mt-4 rounded-xl" onClick={() => (tag ? pickTag(tag) : navigate("/mur"))} data-testid="feed-goto-wall">{tag ? "Retirer le filtre" : "Publier une vidéo"}</Button>
          </div>
        </div>
      ) : (
        <div ref={containerRef} onScroll={onScroll} className="h-full overflow-y-scroll snap-y snap-mandatory" data-testid="feed-container">
          {posts.map((post) => {
            return (
              <section key={post._key} ref={(el) => { itemRefs.current[post.id] = el; }} data-postid={post.id} className="relative h-full w-full snap-start snap-always flex items-center justify-center" data-testid={`feed-item-${post.id}`}>
                <div className="relative h-full w-full max-w-md mx-auto">
                  <PhotoMedia photo={post} className="!h-full" feed active={activeId === post.id} />
                  {/* Right actions */}
                  <div className="absolute right-3 bottom-28 z-20 flex flex-col items-center gap-5 text-white">
                    <div className="flex flex-col items-center">
                      <button onClick={() => toggleLike(post)} className="h-11 w-11 grid place-items-center rounded-full bg-white/10 backdrop-blur active:scale-90 transition-transform" data-testid={`feed-like-${post.id}`} aria-label="J'aime">
                        <Heart className={`h-6 w-6 ${post.liked_by_me ? "fill-[hsl(0_92%_62%)] text-[hsl(0_92%_62%)]" : ""}`} />
                      </button>
                      <span className="text-xs mt-1" data-testid={`feed-like-count-${post.id}`}>{post.like_count}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <button onClick={() => openComments(post)} className="h-11 w-11 grid place-items-center rounded-full bg-white/10 backdrop-blur active:scale-90 transition-transform" data-testid={`feed-comment-${post.id}`} aria-label="Commenter">
                        <MessageCircle className="h-6 w-6" />
                      </button>
                      <span className="text-xs mt-1">{post.comment_count}</span>
                    </div>
                    <button onClick={() => share(post)} className="h-11 w-11 grid place-items-center rounded-full bg-white/10 backdrop-blur active:scale-90 transition-transform" data-testid={`feed-share-${post.id}`} aria-label="Partager">
                      <Share2 className="h-6 w-6" />
                    </button>
                    <button onClick={() => toggleSave(post)} className="h-11 w-11 grid place-items-center rounded-full bg-white/10 backdrop-blur active:scale-90 transition-transform" data-testid={`feed-save-${post.id}`} aria-label="Enregistrer">
                      <Bookmark className={`h-6 w-6 ${post.saved_by_me ? "fill-white text-white" : ""}`} />
                    </button>
                  </div>
                  {/* Bottom info */}
                  <div className="absolute left-3 right-16 bottom-6 z-20 text-white">
                    <button onClick={() => navigate(`/c/${post.sender?.username}`)} className="flex items-center gap-2 mb-2" data-testid={`feed-creator-${post.id}`}>
                      <Avatar className="h-9 w-9 border border-white/30">
                        {post.sender?.avatar && <AvatarImage src={post.sender.avatar} alt={post.sender.username} />}
                        <AvatarFallback className="bg-accent text-accent-foreground text-xs">{initials(post.sender?.username)}</AvatarFallback>
                      </Avatar>
                      <span className="font-semibold">@{post.sender?.username}</span>
                    </button>
                    {post.caption && <p className="text-sm text-white/90 line-clamp-2">{post.caption}</p>}
                    {post.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {post.hashtags.map((h) => (
                          <button key={h} onClick={() => pickTag(h)} className="text-xs text-[hsl(214_100%_75%)] font-medium">#{h}</button>
                        ))}
                      </div>
                    )}
                    {post.sound && (
                      <button onClick={() => navigate(`/son/${encodeURIComponent(post.sound_key || post.sound)}`)} className="flex items-center gap-1.5 mt-2 text-xs text-white/90" data-testid={`feed-sound-${post.id}`}>
                        <span className="h-6 w-6 rounded-full bg-white/15 grid place-items-center overflow-hidden">
                          {post.sound_artwork_url ? <img src={post.sound_artwork_url} alt="" className="h-full w-full object-cover" /> : <Music className="h-3.5 w-3.5" />}
                        </span>
                        <span className="truncate max-w-[200px] animate-pulse">{post.sound}</span>
                      </button>
                    )}
                  </div>
                </div>
              </section>
            );
          })}
          <div className="h-14 grid place-items-center text-white/50 text-xs snap-start">{loadingMore ? "Chargement…" : "Continuez à défiler…"}</div>
        </div>
      )}

      {/* Comments sheet */}
      <Sheet open={!!commentsFor} onOpenChange={(v) => { if (!v) { setCommentsFor(null); setReplyTo(null); } }}>
        <SheetContent side="bottom" className="rounded-t-2xl h-[72vh] flex flex-col" data-testid="feed-comments-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">Commentaires</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-3 space-y-4">
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Soyez le premier à commenter.</p>
            ) : comments.map((c) => (
              <div key={c.id} className="space-y-3">
                <CommentRow c={c} />
                {c.replies?.map((r) => <CommentRow key={r.id} c={r} parentId={c.id} isReply />)}
              </div>
            ))}
          </div>
          {replyTo && (
            <div className="flex items-center justify-between text-xs text-muted-foreground bg-secondary rounded-lg px-3 py-1.5 mb-2">
              <span>Réponse à @{replyTo.username}</span>
              <button onClick={() => setReplyTo(null)} data-testid="cancel-reply"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder={replyTo ? `Répondre à @${replyTo.username}…` : "Ajouter un commentaire…"} className="rounded-xl" data-testid="feed-comment-input" />
            <Button size="icon" className="rounded-xl shrink-0" onClick={sendComment} data-testid="feed-comment-send"><Send className="h-5 w-5" /></Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Feed;
